/**
 * Cross-feature lookups (teachers, subjects) — hit the HTTP endpoints
 * directly so we don't import sibling features (architecture invariant).
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

type StaffResponse = components['schemas']['StaffResponse'];
type SubjectResponse = components['schemas']['SubjectResponse'];

// Hand-written, not `components['schemas']['EntityListResult_StaffResponse_']`
// — the checked-in shared/api-types/index.ts is generated from a stale
// OpenAPI dump and still models GET /v1/staff on the old cursor-paginated
// shape it had before the entity-framework migration. Regenerating that
// file surfaces unrelated pre-existing type errors in catalog/packages/
// people (11F.4 scope is the calendar teacher dropdown only — see PR notes).
// `/v1/staff` actually returns `EntityListResult[StaffResponse]`
// (backend/app/features/entity/schemas.py); only `items` is consumed here.
interface PaginatedStaff {
  items: StaffResponse[];
}
interface PaginatedSubjects {
  items: SubjectResponse[];
}

export function useTeacherOptions() {
  return useQuery({
    queryKey: ['calendar', 'lookups', 'teachers'] as const,
    queryFn: () =>
      api.get<PaginatedStaff>('/v1/staff', {
        query: { status: 'active', staff_type: 'teacher', page_size: 100 },
      }),
    staleTime: 60_000,
    select: (data) =>
      data.items.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` })),
  });
}

export function useSubjectOptions() {
  return useQuery({
    queryKey: ['calendar', 'lookups', 'subjects'] as const,
    queryFn: () =>
      api.get<PaginatedSubjects>('/v1/catalog/subjects', { query: { status: 'active' } }),
    staleTime: 60_000,
    select: (data) => data.items.map((s) => ({ value: s.id, label: s.name })),
  });
}

